'use client';

import { useState, useEffect } from 'react';
import { useCurrentUser } from '@/providers/CurrentUserProvider';
import { usePathname } from 'next/navigation';

interface IncompleteApplication {
  token: string;
  mentorUserId: string;
  mentorHandle?: string;
  mentorName?: string;
  status: string;
}

export function useIncompleteApplication() {
  const { user, userLoading } = useCurrentUser();
  const pathname = usePathname();
  const [incompleteApp, setIncompleteApp] = useState<IncompleteApplication | null>(null);
  const [loading, setLoading] = useState(false);

  // Check if user is currently on mentorship application page
  const isOnApplicationPage = pathname?.startsWith('/mentors/') && pathname?.endsWith('/apply');

  useEffect(() => {
    const checkForIncompleteApplication = async () => {
      
      if (userLoading || isOnApplicationPage) {
        return;
      }
      
      setLoading(true);
      
      try {
        // Check localStorage for application token
        const token = localStorage.getItem('mentorshipApplicationToken');
        
        if (!token) {
          setIncompleteApp(null);
          setLoading(false);
          return;
        }

        // Get stored mentor info from localStorage
        const mentorUserId = localStorage.getItem('mentorshipApplicationMentorId');
        
        if (!mentorUserId) {
          localStorage.removeItem('mentorshipApplicationToken');
          setIncompleteApp(null);
          setLoading(false);
          return;
        }

        // Check if application is still valid and incomplete
        const requestBody = { 
          token,
          mentorUserId,
          menteeUserId: user?.id 
        };
        
        const response = await fetch('/api/mentorshipApplications/validate', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(requestBody)
        });

        
        if (response.ok) {
          const data = await response.json();
          if (data.valid && data.application.status === 'DRAFT') {
            setIncompleteApp({
              token,
              mentorUserId: data.application.mentorUserId,
              mentorHandle: data.application.mentorHandle,
              status: data.application.status
            });
          } else {
            // Application is complete or invalid, remove token
            localStorage.removeItem('mentorshipApplicationToken');
            localStorage.removeItem('mentorshipApplicationMentorId');
            setIncompleteApp(null);
          }
        } else {
          // Token is invalid, remove it
          localStorage.removeItem('mentorshipApplicationToken');
          setIncompleteApp(null);
        }
      } catch (error) {
        console.error('Error checking incomplete application:', error);
        setIncompleteApp(null);
      } finally {
        setLoading(false);
      }
    };

    checkForIncompleteApplication();
  }, [user?.id, userLoading, isOnApplicationPage]);

  const dismissApplication = () => {
    localStorage.removeItem('mentorshipApplicationToken');
    localStorage.removeItem('mentorshipApplicationMentorId');
    setIncompleteApp(null);
  };

  const continueApplication = () => {
    if (incompleteApp && incompleteApp.mentorHandle) {
      const url = `/mentors/${incompleteApp.mentorHandle}/apply`;
      window.location.href = url;
    }
  };

  return {
    incompleteApp,
    loading,
    isOnApplicationPage,
    dismissApplication,
    continueApplication
  };
}
